// Batch 5, Warnings & Approvals: the settings form <-> the v1/warningSettings body.
import moment from 'moment'

// The checks, as the log and its filter name them.
export const CHECKS = [
  { code: 'VALUE_RATIO', label: 'Bigger than the previous version' },
  { code: 'VALUE_BEFORE_EDIT', label: 'Bigger than before the edit' },
  { code: 'VALUE_OVER', label: 'Over the limit' },
  { code: 'TWO_ACTIVE', label: 'Two Active quotes' }
]

export const SOURCES = { ON_SAVE: 'On save', NIGHTLY: 'Nightly scan' }

export function checkLabel(code) {
  var c = CHECKS.find(x => x.code === code)
  return c ? c.label : code
}

// The keys the PUT takes (it answers 400 to any other). Keep in step with the backend.
export const SETTINGS_KEYS = ['valueRatioEnabled', 'valueRatioY', 'valueOverEnabled', 'valueOverX', 'twoActiveEnabled',
  'nightlyEnabled', 'recipientUserIds', 'approvalRecipientUserIds', 'approvers',
  'noExecUnlinkedEnabled', 'noExecMissingEnabled', 'noExecSalesWindowMonths', 'dupProjectsAlertEnabled', 'dupAlertExtraUserIds']

// BE4 contract: decision 2's two no-sales-exec checks (with the sales window they look back over) and the duplicate
// projects alert. Each group is sent only when the backend's GET has all its keys, so a backend without BE4 (whose
// PUT answers 400 to any other key) still saves the rest.
export const NO_EXEC_KEYS = ['noExecUnlinkedEnabled', 'noExecMissingEnabled', 'noExecSalesWindowMonths']
export const DUP_ALERT_KEYS = ['dupProjectsAlertEnabled', 'dupAlertExtraUserIds']

function hasKeys(saved, keys) {
  return !!saved && keys.every(k => k in saved)
}

export function noExecSupported(saved) {
  return hasKeys(saved, NO_EXEC_KEYS)
}

export function dupAlertSupported(saved) {
  return hasKeys(saved, DUP_ALERT_KEYS)
}

// Which tab each field is on, so a 400 can open the tab with the error.
export const SETTINGS_TABS = [
  {
    key: 'anomaly',
    header: 'Anomaly warnings',
    explain: 'Catch a quote value that looks wrong, when it is saved and overnight.',
    fields: ['valueRatioEnabled', 'valueRatioY', 'valueOverEnabled', 'valueOverX', 'twoActiveEnabled', 'nightlyEnabled', 'recipientUserIds']
  },
  {
    key: 'approval',
    header: 'New customer approval',
    explain: 'Who is emailed when a new customer needs approval, and who can approve.',
    fields: ['approvalRecipientUserIds', 'approvers']
  },
  {
    key: 'noExec',
    header: 'No sales exec',
    explain: 'Accounts in the sales data with no sales exec, listed in the nightly warnings email while it holds.',
    fields: NO_EXEC_KEYS
  },
  {
    key: 'dupAlert',
    header: 'Duplicate projects',
    explain: 'The 07:00 email of new possible duplicate projects, to users with Merge Projects and anyone added here.',
    fields: DUP_ALERT_KEYS
  }
]

export function tabWithError(errors) {
  var keys = Object.keys(errors || {})
  var t = SETTINGS_TABS.find(tab => tab.fields.some(f => keys.indexOf(f) > -1))
  return t ? t.key : null
}

function ids(list) {
  return Array.isArray(list) ? list.map(Number).filter(n => !isNaN(n)) : []
}

function num(value, fallback) {
  var n = Number(value)
  return value === null || value === undefined || value === '' || isNaN(n) ? fallback : n
}

/** The saved settings as the form edits them (the recommended defaults when there are none). */
export function toForm(saved) {
  var s = saved || {}
  return {
    valueRatioEnabled: s.valueRatioEnabled !== false,
    valueRatioY: num(s.valueRatioY, 10),
    valueOverEnabled: s.valueOverEnabled === true,
    valueOverX: num(s.valueOverX, 1000000),
    twoActiveEnabled: s.twoActiveEnabled !== false,
    nightlyEnabled: s.nightlyEnabled !== false,
    recipientUserIds: ids(s.recipientUserIds),
    approvalRecipientUserIds: ids(s.approvalRecipientUserIds),
    approvers: (s.approvers || []).map(a => ({
      userId: a.userId,
      name: a.name,
      canApprove: a.canApprove === true,
      fromRole: a.fromRole !== false,
      reset: false
    })),
    noExecUnlinkedEnabled: s.noExecUnlinkedEnabled === true,
    noExecMissingEnabled: s.noExecMissingEnabled === true,
    noExecSalesWindowMonths: num(s.noExecSalesWindowMonths, 12),
    dupProjectsAlertEnabled: s.dupProjectsAlertEnabled !== false,
    dupAlertExtraUserIds: ids(s.dupAlertExtraUserIds)
  }
}

/**
 * Only the approvers that changed: `{ userId, reset: true }` for "Reset to role", otherwise
 * `{ userId, canApprove }` when the tick differs from the saved one.
 */
export function approverChanges(approvers, savedApprovers) {
  var saved = {}
  ;(savedApprovers || []).forEach(a => { saved[a.userId] = a })
  var out = []
  ;(approvers || []).forEach(a => {
    if (a.reset) {
      out.push({ userId: a.userId, reset: true })
    } else if (!saved[a.userId] || saved[a.userId].canApprove !== a.canApprove) {
      out.push({ userId: a.userId, canApprove: a.canApprove === true })
    }
  })
  return out
}

/** The PUT body: every setting, and the approvers only when one changed. */
export function toPayload(form, saved) {
  var f = form || {}
  var body = {
    valueRatioEnabled: f.valueRatioEnabled === true,
    valueRatioY: num(f.valueRatioY, null),
    valueOverEnabled: f.valueOverEnabled === true,
    valueOverX: num(f.valueOverX, null),
    twoActiveEnabled: f.twoActiveEnabled === true,
    nightlyEnabled: f.nightlyEnabled === true,
    recipientUserIds: ids(f.recipientUserIds),
    approvalRecipientUserIds: ids(f.approvalRecipientUserIds)
  }
  var changes = approverChanges(f.approvers, saved && saved.approvers)
  if (changes.length) body.approvers = changes
  if (noExecSupported(saved)) {
    body.noExecUnlinkedEnabled = f.noExecUnlinkedEnabled === true
    body.noExecMissingEnabled = f.noExecMissingEnabled === true
    body.noExecSalesWindowMonths = num(f.noExecSalesWindowMonths, null)
  }
  if (dupAlertSupported(saved)) {
    body.dupProjectsAlertEnabled = f.dupProjectsAlertEnabled === true
    body.dupAlertExtraUserIds = ids(f.dupAlertExtraUserIds)
  }
  return body
}

/** The same checks the server makes, so a bad number is caught before the request. */
export function validate(form) {
  var errors = {}
  var y = Number(form.valueRatioY)
  if (form.valueRatioY === null || form.valueRatioY === '' || isNaN(y) || y <= 1) {
    errors.valueRatioY = 'Must be more than 1.'
  }
  var x = Number(form.valueOverX)
  if (form.valueOverX === null || form.valueOverX === '' || isNaN(x) || x <= 0) {
    errors.valueOverX = 'Must be more than £0.'
  }
  var m = Number(form.noExecSalesWindowMonths)
  if (form.noExecSalesWindowMonths === null || form.noExecSalesWindowMonths === '' || isNaN(m) || m < 1 || m > 60 ||
    m !== Math.floor(m)) {
    errors.noExecSalesWindowMonths = 'Must be a whole number of months from 1 to 60.'
  }
  return errors
}

/** "from role", or "set here" for a user's own setting; a pending reset reads as the role's. */
export function approverNote(a) {
  if (!a) return ''
  if (a.reset) return 'from role (on save)'
  return a.fromRole ? 'from role' : 'set here'
}

// The backend sends UK "dd/MM/yyyy HH:mm" (plus an ISO twin, e.g. lastScanAtIso). Parse strictly: a bare moment(value)
// reads "04/10/2026" as 10 April.
export function formatDateTime(value) {
  if (!value) return ''
  var m = moment(value, ['DD/MM/YYYY HH:mm', moment.ISO_8601], true)
  return m.isValid() ? m.format('DD/MM/YYYY HH:mm') : String(value)
}

export function scanSummary(result) {
  var r = result || {}
  var found = Number(r.found) || 0
  var logged = Number(r.logged) || 0
  return 'Found ' + found + ', new ' + logged + '. ' + (r.emailed ? 'A summary email was sent.' : 'No email was sent.')
}
